import { gstInclusiveBreakdown, formatReceiptInr } from './receiptGstBreakdown.js';
import { DEFAULT_HEADER_LOGO } from './siteSettingsJson.js';
import { resolveJoinPackageOrderSummary } from './joinPackageDuration.js';
import { resolveIndexedBookingParticipants } from './customerDisplayName.js';

const ORG = {
  name: 'Invincible',
  addressLines: [
    '308, 3rd Floor, University Plaza,',
    'Vijay Cross Roads, Navrangpura,',
    'Ahmedabad, Gujarat 380009'
  ],
  gstin: '24AABTI1271J1ZU',
  hsn: '996313'
};

function resolveRegistrationIdDisplay(booking) {
  const id = booking?.registrationId ?? booking?.registration?.id ?? null;
  return id != null && id !== '' ? String(id) : '—';
}

function parseGatewayChargeFromNotes(notes) {
  const m = String(notes || '').match(/Gateway:\s*([0-9]+(?:\.[0-9]+)?)/i);
  const n = m ? Number(m[1]) : 0;
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0;
}

function formatShortDate(d) {
  if (!d) return '—';
  const x = new Date(d);
  return Number.isNaN(x.getTime())
    ? '—'
    : x.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatEventDateRange(batch) {
  if (!batch?.startDate && !batch?.endDate) return '—';
  const start = formatShortDate(batch.startDate);
  const end = formatShortDate(batch.endDate);
  if (start === end || !batch.endDate) return start;
  return `${start} – ${end}`;
}

function participantAge(traveler) {
  const raw = traveler?.dateOfBirth || traveler?.birthdate || traveler?.dob || null;
  if (!raw) return '—';
  const d = new Date(String(raw).slice(0, 10));
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 && age <= 130 ? String(age) : '—';
}

function resolvePackage(booking, trek) {
  if (booking?.joinFromPackage?.id) return booking.joinFromPackage;
  if (booking?.registration?.package?.id) return booking.registration.package;

  const packages = trek?.packages || [];
  const joinId = booking?.joinFromPackageId ?? booking?.registration?.packageId ?? null;
  if (joinId) {
    const fromTrek = packages.find((p) => String(p.id) === String(joinId));
    if (fromTrek) return fromTrek;
  }
  return packages.find((p) => p.isMain) || packages[0] || null;
}

function sumGatewayFromReceipts(receipts) {
  const list = Array.isArray(receipts) ? receipts : [];
  return list.reduce((sum, r) => {
    if (String(r?.method || '').toLowerCase() !== 'online') return sum;
    return sum + parseGatewayChargeFromNotes(r?.notes);
  }, 0);
}

function receiptStyles() {
  return `
    body { font-family: -apple-system, BlinkMacSystemFont, system-ui, sans-serif; margin: 0; padding: 32px 16px; background: #f3f4f6; }
    .invoice-shell { max-width: 720px; margin: 0 auto; background: #ffffff; padding: 32px 40px 40px; box-shadow: 0 10px 30px rgba(15, 23, 42, 0.12); }
    .invoice-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 24px; }
    .logo-wrap img { height: 56px; max-width: 200px; object-fit: contain; }
    .title { font-size: 24px; color: #0f172a; font-weight: 600; text-align: right; }
    .muted { color: #6b7280; font-size: 12px; }
    .meta-row { display: flex; justify-content: space-between; font-size: 13px; margin-bottom: 16px; flex-wrap: wrap; gap: 8px; }
    .meta-label { color: #6b7280; }
    .meta-value { color: #111827; font-weight: 500; }
    .section { margin-top: 20px; }
    .section-title { font-size: 13px; font-weight: 600; margin-bottom: 4px; }
    .section-block { border: 1px solid #e5e7eb; border-radius: 4px; padding: 12px 14px; font-size: 13px; }
    .event-detail-row { margin: 4px 0; }
    .event-detail-row strong { color: #374151; min-width: 88px; display: inline-block; }
    .two-col { display: flex; justify-content: space-between; gap: 24px; }
    .two-col > div { flex: 1; }
    .org-name { font-weight: 600; margin-bottom: 4px; }
    .address-line { margin: 0; color: #4b5563; font-size: 12px; }
    .org-meta { margin-top: 8px; color: #4b5563; font-size: 12px; }
    .org-meta strong { font-weight: 600; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    .summary-table { margin-top: 12px; }
    .summary-table th { background: #f9fafb; font-weight: 600; text-align: left; padding: 8px 10px; border-bottom: 1px solid #e5e7eb; }
    .summary-table td { padding: 8px 10px; border-bottom: 1px solid #e5e7eb; }
    .summary-table td.amount { text-align: right; white-space: nowrap; }
    .summary-table tfoot td { font-weight: 700; }
    .participants-table { margin-top: 8px; }
    .participants-table th { background: #f9fafb; font-weight: 600; text-align: left; padding: 8px 10px; border-bottom: 1px solid #e5e7eb; }
    .participants-table td { padding: 8px 10px; border-bottom: 1px solid #e5e7eb; }
    .note { margin-top: 18px; font-size: 11px; color: #4b5563; }
    .note strong { font-weight: 600; }
    @media print {
      body { background: #ffffff; padding: 0; }
      .invoice-shell { box-shadow: none; margin: 0; max-width: 100%; }
    }
  `;
}

/**
 * Generate Tax Invoice HTML for a booking (customer My Bookings / confirmed page).
 */
export function getBookingInvoiceHtml(booking) {
  if (!booking) return '';

  const trek = booking.batch?.trek;
  const pkg = resolvePackage(booking, trek);
  const indexed = resolveIndexedBookingParticipants(booking);
  const travelers = Array.isArray(booking.travelers)
    ? booking.travelers.filter((t) => t && typeof t === 'object')
    : [];
  // Group online bookings: show every sibling on one invoice (not only this row's traveler).
  const participants = indexed.length > 1
    ? indexed.map((row) => ({
        index: row.index,
        name: row.name,
        age: '—'
      }))
    : travelers.length
      ? travelers.map((t, i) => ({
          index: i + 1,
          name: [t.firstName, t.lastName].filter(Boolean).join(' ') || 'Guest Traveller',
          age: participantAge(t)
        }))
      : [{
          index: 1,
          name: [booking.registration?.firstName, booking.registration?.lastName].filter(Boolean).join(' ') || 'Guest Traveller',
          age: '—'
        }];

  const primaryName =
    participants.length > 1
      ? participants.map((p) => p.name).filter(Boolean).join(', ')
      : participants[0]?.name || 'Guest Traveller';
  const bookingCode = booking.bookingCode || '';
  const registrationIdDisplay = resolveRegistrationIdDisplay(booking);
  const tripName = trek?.name || '—';
  const mainPackage = (trek?.packages || []).find((p) => p.isMain) || pkg;
  const packageSummary = resolveJoinPackageOrderSummary(pkg, {
    mainPackage,
    batchStartDate: booking.batch?.startDate,
    batchEndDate: booking.batch?.endDate,
    allScheduleDays: trek?.scheduleDays || trek?.schedule_days || []
  });
  const packageDates = formatEventDateRange({
    startDate: packageSummary.startDate,
    endDate: packageSummary.endDate
  });
  const packageName = pkg?.name || '—';
  const packageDesc = String(pkg?.description || '').trim();

  const invoiceDate = formatShortDate(booking.createdAt || new Date());
  const baseTotal = Number(booking.totalAmount ?? 0);
  const advanceAmount = Number(booking.advanceAmount ?? 0);
  const paidOnline = Number(booking.amountPaidOnline ?? 0);
  const paidOffice = Number(booking.amountPaidAtOffice ?? 0);
  const totalPaid = paidOnline + paidOffice;
  const gatewayCharge = sumGatewayFromReceipts(booking.receipts);
  const status = String(booking.status || '');
  const isAdvanceMarkedPaid = ['advance_paid', 'remaining_pending'].includes(status);
  const isFullyPaid = status === 'confirmed' || status === 'fully_paid';
  const hasAnyPayment = totalPaid > 0 || isAdvanceMarkedPaid;

  const invoiceInclusiveAmount = isFullyPaid
    ? baseTotal
    : hasAnyPayment
      ? Math.min(totalPaid > 0 ? totalPaid : advanceAmount, advanceAmount || totalPaid || baseTotal)
      : Math.min(advanceAmount || baseTotal, baseTotal);

  const { subTotal, sgst, cgst, gstTotal, grandTotal } = gstInclusiveBreakdown(invoiceInclusiveAmount);
  const effectivePaidForPending = totalPaid > 0 ? totalPaid : (isAdvanceMarkedPaid ? advanceAmount : 0);
  const pendingAmount = Math.max(0, baseTotal - effectivePaidForPending);
  const totalPaidInclGateway = totalPaid + gatewayCharge;

  const invoiceTitle = isFullyPaid
    ? 'Final Tax Invoice'
    : hasAnyPayment
      ? 'Advance Payment Tax Invoice'
      : 'Advance Pending Invoice';
  const invoiceScopeLabel = isFullyPaid
    ? 'Final settlement'
    : hasAnyPayment
      ? 'Advance payment received'
      : 'Advance payment pending';
  const paymentMethodLabel = hasAnyPayment
    ? (paidOnline > 0 && paidOffice > 0
      ? 'Online + Office'
      : paidOnline > 0
        ? 'Online (Razorpay)'
        : 'Pay at Office')
    : 'Pay at Office (Pending)';

  const logoUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}${DEFAULT_HEADER_LOGO}`;

  const participantRowsHtml = participants
    .map(
      (p) => `
          <tr>
            <td>${p.index}</td>
            <td>${p.name}</td>
            <td>${p.age}</td>
          </tr>`
    )
    .join('');

  const gatewayRow =
    gatewayCharge > 0
      ? `
          <tr>
            <td>Bank / Payment Gateway Charges (2%)</td>
            <td class="amount">₹ ${formatReceiptInr(gatewayCharge)}/-</td>
          </tr>`
      : '';

  const totalFooter =
    gatewayCharge > 0 && totalPaid > 0
      ? `
          <tr>
            <td>Total Amount Paid <span class="muted">(incl. bank charges)</span></td>
            <td class="amount">₹ ${formatReceiptInr(totalPaidInclGateway)}/-</td>
          </tr>`
      : `
          <tr>
            <td>Total <span class="muted">(GST inclusive)</span></td>
            <td class="amount">₹ ${formatReceiptInr(grandTotal)}/-</td>
          </tr>`;

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Tax Invoice ${bookingCode}</title>
  <style>${receiptStyles()}</style>
</head>
<body>
  <div class="invoice-shell">
    <div class="invoice-header">
      <div class="logo-wrap">
        <img src="${logoUrl}" alt="Invincible logo" />
      </div>
      <div class="title">${invoiceTitle}</div>
    </div>

    <div class="meta-row">
      <div>
        <span class="meta-label">Date:</span>
        <span class="meta-value">${invoiceDate}</span>
      </div>
      <div>
        <span class="meta-label">Registration ID:</span>
        <span class="meta-value">${registrationIdDisplay}</span>
      </div>
    </div>

    <div class="two-col section">
      <div>
        <div class="section-title">Invoiced To:</div>
        <div class="section-block">
          <div class="org-name">${primaryName}</div>
        </div>
      </div>
      <div>
        <div class="section-title">Pay To:</div>
        <div class="section-block">
          <div class="org-name">${ORG.name}</div>
          ${ORG.addressLines.map((l) => `<p class="address-line">${l}</p>`).join('')}
          <div class="org-meta">
            <div><strong>GSTIN:</strong> ${ORG.gstin}</div>
            <div><strong>HSN Code:</strong> ${ORG.hsn}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">Event Details</div>
      <div class="section-block">
        <div class="event-detail-row"><strong>Event:</strong> ${tripName}</div>
        <div class="event-detail-row"><strong>Package:</strong> ${packageName}${packageDesc ? ` — ${packageDesc}` : ''}</div>
        <div class="event-detail-row"><strong>Date:</strong> ${packageDates}</div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">Payment</div>
      <div class="section-block muted">
        Method: <strong>${paymentMethodLabel}</strong> · Status: <strong>${invoiceScopeLabel}</strong>
        ${gatewayCharge > 0 ? ` · Bank charges: <strong>₹ ${formatReceiptInr(gatewayCharge)}</strong>` : ''}
      </div>
    </div>

    <div class="section">
      <div class="section-title">Amount Summary</div>
      <table class="summary-table">
        <thead>
          <tr>
            <th>Details</th>
            <th class="amount">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>${tripName} — ${packageName} <span class="muted">(${invoiceScopeLabel}, incl. 5% GST)</span></td>
            <td class="amount">₹ ${formatReceiptInr(grandTotal)}/-</td>
          </tr>
          <tr>
            <td>Taxable amount <span class="muted">(excl. 5% GST)</span></td>
            <td class="amount">₹ ${formatReceiptInr(subTotal)}/-</td>
          </tr>
          <tr>
            <td>SGST @ 2.5%</td>
            <td class="amount">₹ ${formatReceiptInr(sgst)}/-</td>
          </tr>
          <tr>
            <td>CGST @ 2.5%</td>
            <td class="amount">₹ ${formatReceiptInr(cgst)}/-</td>
          </tr>
          <tr>
            <td>GST total <span class="muted">(5%)</span></td>
            <td class="amount">₹ ${formatReceiptInr(gstTotal)}/-</td>
          </tr>
          ${gatewayRow}
        </tbody>
        <tfoot>${totalFooter}
        </tfoot>
      </table>
      ${!isFullyPaid ? `<p class="note"><strong>Pending Amount:</strong> ₹ ${formatReceiptInr(pendingAmount)} (final invoice after full payment is recorded).</p>` : ''}
    </div>

    <div class="section">
      <div class="section-title">Participants</div>
      <table class="participants-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Age</th>
          </tr>
        </thead>
        <tbody>${participantRowsHtml}
        </tbody>
      </table>
    </div>

    <p class="note">
      <strong>NOTE:</strong> This is a computer generated receipt and does not require physical signature.
    </p>
  </div>

  <script>
    window.onload = function () { window.print(); };
  </script>
</body>
</html>`;
}

export function openBookingInvoice(booking) {
  if (!booking) return;
  const html = getBookingInvoiceHtml(booking);
  if (!html) return;
  const w = window.open('', '_blank', 'width=900,height=1000');
  if (w) {
    w.document.write(html);
    w.document.close();
  } else {
    alert('Please allow pop-ups to download the invoice.');
  }
}
